// store/slices/paymentSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as paymentApi from '../../services/paymentApi';
import {
  PaymentState,
  PaymentInitRequest,
  DueAmountData,
  PaymentInitData,
  PaymentData,
  PaymentStatusResponse,
  DueAmountResponse,
  PaymentInitResponse,
} from '../../types/payment.types';

const initialState: PaymentState = {
  dueAmount: null,
  currency: '051',
  accountType: '',
  currentPayment: null,
  paymentUrl: null,
  isLoading: false,
  error: null,
};

// Async thunk: Fetch due amount
export const fetchDueAmount = createAsyncThunk<
  DueAmountData,
  void,
  { rejectValue: string }
>(
  'payment/fetchDueAmount',
  async (_, { rejectWithValue }) => {
    try {
      const response: DueAmountResponse = await paymentApi.getDueAmount();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch due amount'
      );
    }
  }
);

// Async thunk: Initialize payment
export const initializePayment = createAsyncThunk<
  PaymentInitData,
  PaymentInitRequest | undefined,
  { rejectValue: string }
>(
  'payment/initialize',
  async (data = {}, { rejectWithValue }) => {
    try {
      const response: PaymentInitResponse =
        await paymentApi.initializePayment(data);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to initialize payment'
      );
    }
  }
);

// Async thunk: Fetch payment status
export const fetchPaymentStatus = createAsyncThunk<
  PaymentData,
  string,
  { rejectValue: string }
>(
  'payment/fetchStatus',
  async (paymentId, { rejectWithValue }) => {
    try {
      const response: PaymentStatusResponse =
        await paymentApi.getPaymentStatus(paymentId);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch payment status'
      );
    }
  }
);

// Async thunk: Fetch payment history
export const fetchPaymentHistory = createAsyncThunk<
  PaymentData[],
  void,
  { rejectValue: string }
>(
  'payment/fetchHistory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentApi.getPaymentHistory();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch payment history'
      );
    }
  }
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearPaymentError: (state) => {
      state.error = null;
    },
    clearCurrentPayment: (state) => {
      state.currentPayment = null;
      state.paymentUrl = null;
    },
    resetPaymentState: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch due amount
    builder
      .addCase(fetchDueAmount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDueAmount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dueAmount = action.payload.amount;
        state.currency = action.payload.currency;
        state.accountType = action.payload.accountType;
      })
      .addCase(fetchDueAmount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Initialize payment
    builder
      .addCase(initializePayment.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializePayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentUrl = action.payload.paymentUrl;
      })
      .addCase(initializePayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch payment status
    builder
      .addCase(fetchPaymentStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPayment = action.payload;
      })
      .addCase(fetchPaymentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch payment history
    builder
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearPaymentError, 
  clearCurrentPayment, 
  resetPaymentState 
} = paymentSlice.actions;

export default paymentSlice.reducer;